import React, { useState } from 'react';
import PropTypes from 'prop-types';
import * as XLSX from 'xlsx';
import { intl } from 'containers/LanguageProvider/IntlGlobalProvider';
import projectServiceShape from 'services/types/projectService';
import enhanceWithServices from 'hocs/enhanceWithServices';
import messages from './messages';
import { DownloadButton, StyledCSVDownloader } from './styles';
import { FileTypes } from 'constants/enums';

const ImportReportDownload = ({
  courseName,
  ingestionId,
  projectService,
  ingestionStats,
  ingestionType,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const summary = Object.keys(ingestionStats).map((key) => {
    const [errorOrWarning, errorType] = key.split(':');
    const errorCount = ingestionStats[key];
    return ingestionType === FileTypes.EPUB ? {
      [intl.formatMessage(messages.errorOrWarning)]: `${errorCount} ${errorOrWarning}`,
      [intl.formatMessage(messages.errorType)]: errorType,
    } : {
      [intl.formatMessage(messages.errorOrWarningorFatal)]: `${errorCount} ${errorOrWarning}`,
      [intl.formatMessage(messages.errorType)]: errorType,
    };
  });

  const columnWidth = [
    {wch:30},
    {wch:70},
    {wch:200},
    {wch:15},
    {wch:40},
  ]; 

  const addDataToXLSX = (sheetData) => {
    const workbook = XLSX.utils.book_new();

    sheetData.map((sheet) => {
      if(sheet.data.length){
        const newSheet = XLSX.utils.json_to_sheet(sheet.data);
        newSheet['!cols'] = sheet.columnWidth;

        XLSX.utils.book_append_sheet(workbook, newSheet, sheet.sheetName);
      }
    });

    XLSX.writeFile(workbook, `${courseName}.xlsx`);
  };

  const generateXLSXFile = async() => {
    const response = await projectService.getIngestionLogs(ingestionId);

    const eventTypeFatal = response?.ingestionEvents.filter((value) => value.eventType === 'FATAL');

    const newCourseName = courseName.replace(/[^a-zA-Z1-9-_]/g, "");
    const updatedCourseName = newCourseName.length > 31 ? `${newCourseName.substring(0,28)}...` : newCourseName;

    const sheetData = [
      {
        sheetName: [intl.formatMessage(messages.summary)],
        data: summary,
        columnWidth: [{wch:30},{wch:40}],
      },
      {
        sheetName: [intl.formatMessage(messages.fatal)],
        data: formatEventLog(eventTypeFatal ?? []),
        columnWidth: columnWidth,
      },
      {
        sheetName: updatedCourseName,
        data: formatEventLog(response?.ingestionEvents ?? []),
        columnWidth : columnWidth,
      },
    ];

    addDataToXLSX(sheetData);
  };

  const onDownload = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsLoading(true);
    generateXLSXFile();
    setIsLoading(false);
  };

  /**
   * Formats the action, message and location containing the comma(,) to a
   * corresponding non breaking unicode (\uff0c: fullwidth comma) for csv download.
   * @param {Array} Event Logs
   * @returns Formatted array of objects
   */
  const formatEventLog = (events) =>
    events.map((event) => ({
      [intl.formatMessage(messages.errorType)] : event.error,
      [intl.formatMessage(messages.errorDescription)]: event?.action ?? "".replace(/, /g, "\uff0c"),
      [intl.formatMessage(messages.location)] : event?.location ?? "".replace(/, /g, "\uff0c"),
      [intl.formatMessage(messages.eventType)]: event?.eventType,
      [intl.formatMessage(messages.error)]: event?.message ?? "".replace(/, /g, "\uff0c"),
    }));

  return (
    <StyledCSVDownloader>
      <DownloadButton
        loadable
        isLoading={isLoading}
        primary
        onClick={onDownload}
      >
        {intl.formatMessage(messages.downloadFile)}
      </DownloadButton>
    </StyledCSVDownloader>
  );
};

ImportReportDownload.propTypes = {
  courseName: PropTypes.string,
  ingestionId: PropTypes.string,
  projectService: projectServiceShape,
  ingestionStats: PropTypes.object,
  ingestionType: PropTypes.string,
};

export default enhanceWithServices(ImportReportDownload);
